import React, { useEffect, useState } from 'react';
import { Table, Input, DatePicker, Card, Space, Tag, Button } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { smsApi } from '../../services/api';
import type { SMS } from '../../types';
import dayjs, { Dayjs } from 'dayjs';

const { RangePicker } = DatePicker;
const { Search } = Input;

const SMSViewer: React.FC = () => {
  const [messages, setMessages] = useState<SMS[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchNumber, setSearchNumber] = useState('');
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });

  useEffect(() => {
    loadMessages();
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      loadMessages();
    }, 30000);
    return () => clearInterval(interval);
  }, [pagination.current, pagination.pageSize]);

  const loadMessages = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: pagination.current,
        limit: pagination.pageSize,
      };

      if (searchNumber) {
        params.phoneNumber = searchNumber;
      }

      if (dateRange[0] && dateRange[1]) {
        params.startDate = dateRange[0].format('YYYY-MM-DD');
        params.endDate = dateRange[1].format('YYYY-MM-DD');
      }

      const data = await smsApi.getSMS(params);
      setMessages(data.messages || []);
      setPagination({
        ...pagination,
        total: data.total,
        current: data.page,
      });
    } catch (error) {
      console.error('Failed to load SMS:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    loadMessages();
  };

  const handleTableChange = (newPagination: any) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      total: pagination.total,
    });
  };

  const columns = [
    {
      title: 'Time',
      dataIndex: 'receivedAt',
      key: 'receivedAt',
      width: 180,
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm:ss'),
      sorter: (a: SMS, b: SMS) => dayjs(a.receivedAt).unix() - dayjs(b.receivedAt).unix(),
    },
    {
      title: 'Phone Number',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      width: 150,
      render: (number: string) => <Tag color="blue">{number}</Tag>,
    },
    {
      title: 'From',
      dataIndex: 'from',
      key: 'from',
      width: 150,
      render: (from: string) => <Tag color="green">{from}</Tag>,
    },
    {
      title: 'Message Content',
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
      render: (content: string) => (
        <div style={{ maxWidth: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {content}
        </div>
      ),
    },
    {
      title: 'Message ID',
      dataIndex: 'messageId',
      key: 'messageId',
      width: 120,
      render: (id: string) => <span style={{ fontSize: 12, color: '#888' }}>{id}</span>,
    },
  ];

  return (
    <div>
      <h1>SMS Messages</h1>

      <Card style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space wrap>
            <Search
              placeholder="Search by phone number"
              allowClear
              prefix={<SearchOutlined />}
              style={{ width: 300 }}
              value={searchNumber}
              onChange={(e) => setSearchNumber(e.target.value)}
              onSearch={handleSearch}
              enterButton
            />
            <RangePicker
              value={dateRange}
              onChange={(dates) => setDateRange(dates as [Dayjs | null, Dayjs | null])}
              style={{ width: 300 }}
            />
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
            >
              Search
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadMessages}
            >
              Refresh
            </Button>
          </Space>

          <div>
            <strong>Total Messages:</strong> {pagination.total}
            <span style={{ marginLeft: 16, color: '#888' }}>
              (Auto-refreshes every 30 seconds)
            </span>
          </div>
        </Space>
      </Card>

      <Table
        columns={columns}
        dataSource={messages}
        rowKey="id"
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} messages`,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        onChange={handleTableChange}
        scroll={{ x: 1000 }}
      />
    </div>
  );
};

export default SMSViewer;
